import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { API_BASE_URL } from './api'

/**
 * Realtime events for one workspace, over Server-Sent Events.
 *
 * The server streams `GET /api/v2/orgs/{slug}/events` and ends every stream
 * after a few minutes by design; `EventSource` reconnects on its own, using the
 * `retry:` the server sent. The stream is a latency improvement, never the
 * source of truth: every feature that listens here also reads from the API on
 * mount and polls slowly, so a tab that never connects still works.
 *
 * Event types and their `data`:
 *
 *  - `notification`  a notification row addressed to this user (see
 *                    `features/notifications/types`).
 *  - `chat.message`  `{ channel_id, message }` — a new, edited or deleted
 *                    message in a conversation this user belongs to.
 *  - `chat.channel`  `{ channel_id, action }` — a conversation was created,
 *                    renamed, had its members changed, or was deleted.
 */
export interface RealtimeEvent<T = unknown> {
  type: string
  data: T
}

type Listener = (event: RealtimeEvent) => void

interface RealtimeValue {
  subscribe: (listener: Listener) => () => void
  /** True while the stream is open. Purely informational. */
  connected: boolean
}

const RealtimeContext = createContext<RealtimeValue | null>(null)

const EVENT_TYPES = ['notification', 'chat.message', 'chat.channel'] as const

export function RealtimeProvider({ slug, children }: { slug: string | null; children: ReactNode }) {
  const listeners = useRef(new Set<Listener>())
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    if (!slug || typeof EventSource === 'undefined') return
    const url = `${API_BASE_URL}/api/v2/orgs/${encodeURIComponent(slug)}/events`
    const source = new EventSource(url, { withCredentials: true })
    const forward = (type: string) => (raw: Event) => {
      const message = raw as MessageEvent<string>
      let data: unknown
      try {
        data = JSON.parse(message.data)
      } catch {
        return
      }
      for (const listener of listeners.current) listener({ type, data })
    }
    const handlers = EVENT_TYPES.map((type) => [type, forward(type)] as const)
    for (const [type, handler] of handlers) source.addEventListener(type, handler)
    source.onopen = () => setConnected(true)
    source.onerror = () => setConnected(false)
    return () => {
      for (const [type, handler] of handlers) source.removeEventListener(type, handler)
      source.close()
      setConnected(false)
    }
  }, [slug])

  const subscribe = useCallback((listener: Listener) => {
    listeners.current.add(listener)
    return () => {
      listeners.current.delete(listener)
    }
  }, [])

  return <RealtimeContext.Provider value={{ subscribe, connected }}>{children}</RealtimeContext.Provider>
}

/**
 * Runs `handler` for every event whose type is in `types`.
 *
 * The latest handler is always the one called, so callers can pass an inline
 * closure without worrying about stale state. Outside a `RealtimeProvider`
 * this is a no-op, which keeps components usable in tests and in the
 * workspace-less routes.
 */
export function useRealtimeEvent<T = unknown>(
  types: string | readonly string[],
  handler: (data: T, type: string) => void,
) {
  const context = useContext(RealtimeContext)
  const latest = useRef(handler)
  latest.current = handler
  const key = Array.isArray(types) ? types.join('|') : String(types)

  useEffect(() => {
    if (!context) return
    const wanted = new Set(key.split('|'))
    return context.subscribe((event) => {
      if (wanted.has(event.type)) latest.current(event.data as T, event.type)
    })
  }, [context, key])
}

/** Whether the realtime stream is currently open. */
export function useRealtimeConnected(): boolean {
  return useContext(RealtimeContext)?.connected ?? false
}
